import { Checkbox, FormControlLabel, Grid, TextField, Tooltip } from '@material-ui/core';
import {
  DEFAULT_MEMORY,
  DEFAULT_TIMEOUT,
  DEFAULT_SNAPSTART_ENABLED,
  DEFAULT_SNAP_ALIAS_ENABLED,
  LAMBDA_MEMORY_MB_INPUT_PROPS,
  LAMBDA_TIMEOUT_SECONDS_INPUT_PROPS,
} from 'config-editor-common';

interface MemoryTimeoutSnapstartFieldsProps {
  memory: number;
  timeout: number;
  snapstartEnabled: boolean;
  snapAliasEnabled: boolean;
  onMemoryChange: (value: number) => void;
  onTimeoutChange: (value: number) => void;
  onSnapstartChange: (checked: boolean) => void;
  onSnapAliasChange: (checked: boolean) => void;
  memoryLabel?: string;
  timeoutLabel?: string;
  snapstartLabel?: string;
  snapAliasLabel?: string;
  showInGrid?: boolean;
  /** Disable the SnapStart toggle (e.g. LMI lambdas don't support SnapStart). */
  snapstartDisabled?: boolean;
  /** Tooltip shown over the SnapStart control when disabled. */
  snapstartDisabledReason?: string;
  /**
   * Lambda name — when set, each field gets a `lambda.<name>.<field>` data-ai-anchor so the AI
   * copilot's coach-mark can point at the exact field (memory_size / timeout / snapstart).
   */
  anchorName?: string;
}

export const MemoryTimeoutSnapstartFields = ({
  memory,
  timeout,
  snapstartEnabled,
  snapAliasEnabled,
  onMemoryChange,
  onTimeoutChange,
  onSnapstartChange,
  onSnapAliasChange,
  memoryLabel = 'Memory (MB)',
  timeoutLabel = 'Timeout (seconds)',
  snapstartLabel = 'SnapStart',
  snapAliasLabel = 'Create snap alias',
  showInGrid = true,
  snapstartDisabled = false,
  snapstartDisabledReason,
  anchorName,
}: MemoryTimeoutSnapstartFieldsProps) => {
  const fieldAnchor = (field: string) =>
    anchorName ? { 'data-ai-anchor': `lambda.${anchorName}.${field}` } : {};
  // The snap alias is mandatory (forced checked + locked) in two cases:
  //  - SnapStart is enabled, or
  //  - the lambda is an LMI lambda (capacity provider set => snapstartDisabled).
  // Otherwise it is freely editable and can be created even with SnapStart off.
  const snapstartActive = !snapstartDisabled && (snapstartEnabled ?? DEFAULT_SNAPSTART_ENABLED);
  const snapAliasForced = snapstartDisabled || snapstartActive;
  const snapAliasChecked = snapAliasForced ? true : snapAliasEnabled ?? DEFAULT_SNAP_ALIAS_ENABLED;
  const memoryField = (
    <TextField
      label={memoryLabel}
      type="number"
      value={memory ?? DEFAULT_MEMORY}
      onChange={e => {
        const numValue = e.target.value === '' ? DEFAULT_MEMORY : parseInt(e.target.value, 10);
        if (isNaN(numValue)) {
          onMemoryChange(DEFAULT_MEMORY);
        } else {
          onMemoryChange(numValue);
        }
      }}
      fullWidth
      variant="outlined"
      size="small"
      placeholder={`Default: ${DEFAULT_MEMORY}`}
      inputProps={{ ...LAMBDA_MEMORY_MB_INPUT_PROPS }}
    />
  );

  const timeoutField = (
    <TextField
      label={timeoutLabel}
      type="number"
      value={timeout ?? DEFAULT_TIMEOUT}
      onChange={e => {
        const numValue = e.target.value === '' ? DEFAULT_TIMEOUT : parseInt(e.target.value, 10);
        if (isNaN(numValue)) {
          onTimeoutChange(DEFAULT_TIMEOUT);
        } else {
          onTimeoutChange(numValue);
        }
      }}
      fullWidth
      variant="outlined"
      size="small"
      placeholder={`Default: ${DEFAULT_TIMEOUT}`}
      inputProps={{ ...LAMBDA_TIMEOUT_SECONDS_INPUT_PROPS }}
    />
  );

  const snapstartControl = (
    <FormControlLabel
      control={
        <Checkbox
          checked={snapstartDisabled ? false : snapstartEnabled ?? DEFAULT_SNAPSTART_ENABLED}
          onChange={e => onSnapstartChange(e.target.checked)}
          color="primary"
          disabled={snapstartDisabled}
        />
      }
      label={snapstartLabel}
    />
  );

  const snapstartField =
    snapstartDisabled && snapstartDisabledReason ? (
      <Tooltip title={snapstartDisabledReason}>
        <span>{snapstartControl}</span>
      </Tooltip>
    ) : (
      snapstartControl
    );

  const snapAliasControl = (
    <FormControlLabel
      control={
        <Checkbox
          checked={snapAliasChecked}
          onChange={e => onSnapAliasChange(e.target.checked)}
          color="primary"
          // Locked on while SnapStart is enabled or for LMI lambdas (alias required).
          disabled={snapAliasForced}
        />
      }
      label={snapAliasLabel}
    />
  );

  let snapAliasReason: string | undefined;
  if (snapstartDisabled) {
    snapAliasReason = 'Required for LMI lambdas (capacity provider set).';
  } else if (snapstartActive) {
    snapAliasReason = 'The snap alias will be created automatically — required by SnapStart.';
  }

  const snapAliasField = snapAliasReason ? (
    <Tooltip title={snapAliasReason}>
      <span>{snapAliasControl}</span>
    </Tooltip>
  ) : (
    snapAliasControl
  );

  if (showInGrid) {
    return (
      <>
        <Grid item xs={12} md={4} {...fieldAnchor('memory_size')}>
          {memoryField}
        </Grid>
        <Grid item xs={12} md={4} {...fieldAnchor('timeout')}>
          {timeoutField}
        </Grid>
        <Grid item xs={12} md={4} {...fieldAnchor('snapstart')}>
          {snapstartField}
        </Grid>
        <Grid item xs={12} md={4}>
          {snapAliasField}
        </Grid>
      </>
    );
  }

  return (
    <>
      {memoryField}
      {timeoutField}
      {snapstartField}
      {snapAliasField}
    </>
  );
};
